import React from 'react';
import { Check, AlertCircle, Star, CalendarOff } from 'lucide-react';
import { TrainingStatus } from '../../services/trainingProgressService';

// ── Training-progress status chip (base status is REAL — never excuse-mutated, ADR D3) ──
//
// feat-195: extracted from TrainingProgressPage so that StatusReasonCard can render the
// same chip in its floating header band without importing the page (which imports the
// card — a cycle). One chip, three surfaces: the roster row, the crew dialog, and the
// hover card's header.
//
// Static literal class strings per branch, never interpolated: Tailwind purges classes
// it cannot see as whole tokens in the source, and a purged class fails SILENTLY as an
// unstyled render.
const CHIP: Record<
  TrainingStatus,
  { label: string; tone: string; focusRing: string; Icon: React.ElementType; fill?: string }
> = {
  behind: {
    label: 'Behind',
    tone: 'bg-coral-light text-coral',
    focusRing: 'focus-visible:ring-coral/50',
    Icon: AlertCircle,
  },
  above: {
    label: 'Above & beyond',
    tone: 'bg-success text-white',
    focusRing: 'focus-visible:ring-success/50',
    Icon: Star,
    fill: 'white',
  },
  // feat-112: neutral grey badge for crew with no completed Square shifts in the
  // rolling-14 window. NOT green/red — these crew are excluded from the judgmental
  // buckets. Exact treatment per design-lead: warm cream-dark + inkMuted (delta-flat
  // neutral family, existing tokens) with a hairline ring so it reads as a state
  // against the near-identical page canvas.
  no_recent_shifts: {
    label: 'No recent shifts',
    tone: 'bg-cream-dark text-inkMuted ring-1 ring-inkMuted/20',
    focusRing: 'focus-visible:ring-inkMuted/40',
    Icon: CalendarOff,
  },
  on_track: {
    label: 'On track',
    tone: 'bg-success-light text-success',
    focusRing: 'focus-visible:ring-success/50',
    Icon: Check,
  },
};

// feat-112 copy, now ALSO carried by the feat-195 card as `sentences[0..1]` for the
// no_recent_shifts variant. Kept here for the NON-interactive chip only: when the chip
// is a tooltip anchor this native `title` is DROPPED, because a native title firing
// under a styled card is a double tooltip (design brief §1.4).
const NO_RECENT_SHIFTS_TITLE =
  'No completed shifts on the Square clock in the last 2 weeks — not scored against the last-2-weeks goal.';

export interface StatusChipProps {
  kind: TrainingStatus;
  /**
   * feat-195 D5: make the chip a hover-card anchor. Pass `Boolean(row.status_reason)` —
   * when the backend sent no reason (deploy window, ADR §B5) this MUST stay false and
   * the chip renders exactly as it did before feat-195: no tabIndex, no cursor-help, no
   * data-tooltip-*. A focusable chip that opens an empty card is worse than no
   * affordance — it advertises an explanation and then fails to deliver one (§1.5).
   */
  interactive?: boolean;
  tooltipId?: string;
  tooltipContent?: string;
}

export function StatusChip({ kind, interactive, tooltipId, tooltipContent }: StatusChipProps) {
  const chip = CHIP[kind] ?? CHIP.on_track;
  const { Icon } = chip;
  const base = `inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold ${chip.tone}`;

  if (!interactive) {
    return (
      <span
        className={base}
        title={kind === 'no_recent_shifts' ? NO_RECENT_SHIFTS_TITLE : undefined}
      >
        <Icon size={13} strokeWidth={2.5} fill={chip.fill} /> {chip.label}
      </span>
    );
  }

  return (
    <span
      // `focus: true` is in react-tooltip's DEFAULT open map and its listeners attach
      // directly to each anchor (so non-bubbling focus is fine) — but a <span> is not
      // focusable, so without tabIndex the keyboard half of AC1 silently does nothing.
      // Same affordance the column-header tooltips already use.
      tabIndex={0}
      data-tooltip-id={tooltipId}
      data-tooltip-content={tooltipContent}
      // No dotted underline here: that is the header-LABEL affordance, and on a filled
      // pill it reads as damage. The pill is already a strong affordance; cursor-help
      // plus a status-toned focus ring is enough (design §1.4).
      className={`${base} cursor-help focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${chip.focusRing}`}
    >
      <Icon size={13} strokeWidth={2.5} fill={chip.fill} /> {chip.label}
    </span>
  );
}

export default StatusChip;
